import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { normalizePhone } from "@/lib/cartStorage";

const KEY = "vs_customer";
const CustomerContext = createContext(null);

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || null;
    } catch {
      return null;
    }
  });
  const current = useRef(customer);
  const pending = useRef(null);
  current.current = customer;

  const ensureCartSession = useCallback(async (force = false) => {
    const profile = current.current;
    if (!profile?.phone) throw new Error("Please sign in to save your cart");
    if (!force && profile.cart_token) {
      try {
        const claims = JSON.parse(atob(profile.cart_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        if (claims.type === "cart_access" && claims.exp * 1000 > Date.now() + 60000) return profile;
      } catch { /* The server, not this expiry hint, validates the signature. */ }
    }
    if (pending.current?.phone === profile.phone) return pending.current.promise;
    const promise = api.post("/customers/login", { phone: profile.phone }, { timeout: 15000 }).then(({ data }) => {
      if (normalizePhone(current.current?.phone) !== normalizePhone(profile.phone)) throw new Error("Customer changed");
      current.current = data;
      setCustomer(data);
      return data;
    }).finally(() => {
      if (pending.current?.promise === promise) pending.current = null;
    });
    pending.current = { phone: profile.phone, promise };
    return promise;
  }, []);

  useEffect(() => {
    try {
      if (customer) localStorage.setItem(KEY, JSON.stringify(customer));
      else localStorage.removeItem(KEY);
    } catch { /* A storage quota/privacy restriction must not crash or erase the active session. */ }
  }, [customer]);

  const value = useMemo(
    () => ({ customer, setCustomer, ensureCartSession, signOut: () => setCustomer(null) }),
    [customer, ensureCartSession]
  );
  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export const useCustomer = () => useContext(CustomerContext);
